// tracks whether the API is actually reachable, which is not the same question as
// `navigator.onLine`. the browser flag only knows about the network interface: it reports
// online on a wifi network with no route to the internet, and it has no opinion at all
// about the backend being down. so reachability is derived from real request outcomes,
// with the browser flag used only as a fast negative.

let serverReachable = true
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((listener) => listener())

const setReachable = (value: boolean) => {
  if (serverReachable === value) return
  serverReachable = value
  // probe only while the server is believed to be down, and only while someone is watching
  if (value) stopProbing()
  else if (listeners.size > 0) startProbing()
  notify()
}

// called by the api client on any response — a reply of any status proves a live server
export const reportServerReachable = () => setReachable(true)

// called by the api client when a request failed without a response at all
export const reportServerUnreachable = () => setReachable(false)

export const isOffline = (): boolean =>
  !serverReachable || (typeof navigator !== 'undefined' && navigator.onLine === false)

export function subscribeToConnectivity(listener: () => void): () => void {
  listeners.add(listener)
  if (listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('online', onBrowserOnline)
    window.addEventListener('offline', notify)
    if (!serverReachable) startProbing()
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('online', onBrowserOnline)
      window.removeEventListener('offline', notify)
      stopProbing()
    }
  }
}

// regaining an interface is only a hint that the server may be reachable again. clear the
// flag optimistically so the UI recovers immediately; the next failed request re-sets it.
function onBrowserOnline() {
  setReachable(true)
  notify()
}

// how often to test a server that went away. often enough that a backend restart is noticed
// while the user is still looking at the page, rare enough to be nothing on a dead link.
const PROBE_INTERVAL_MS = 10_000

let probeTimer: ReturnType<typeof setInterval> | undefined

// while offline, nothing else calls the API: the offline page renders no queries, window
// focus refetching is off, and an empty outbox sends nothing. so a backend that comes back
// without any network change — a restart, the likeliest outage for a self-hosted app —
// would leave the UI stuck on "You're offline" indefinitely. this probe is what ends that.
function startProbing(): void {
  if (probeTimer !== undefined || typeof window === 'undefined') return
  probeTimer = setInterval(() => {
    if (!serverReachable) void probeServer()
  }, PROBE_INTERVAL_MS)
}

function stopProbing(): void {
  if (probeTimer === undefined) return
  clearInterval(probeTimer)
  probeTimer = undefined
}

// deliberately not the axios client: this must not carry auth, trip the refresh
// interceptor, or be retried. any answer at all proves the server is up.
async function probeServer(): Promise<void> {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'
  try {
    await fetch(`${base}/health`, { method: 'GET', cache: 'no-store' })
    setReachable(true)
  } catch {
    // still down — the next tick tries again
  }
}
